/**
 * A board row's "Today's list" in send order across ALL its list groups
 * (26/10/2026, user: "here we have everything separated by group list. but
 * ignore that just show them based on time please on each container"): every
 * group's live drops merged into one list by time, then the groups whose list
 * gets no drop ("—" and why), "+N not used today" for every group together,
 * and the hover's lists of today merged the same way — a list in two groups
 * reads once, ×N over both.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { BoardGroupRow } from '../board-list-rows';
import { type DropListLine, listName, type TodayListEntry } from './today-list-order';

export interface ContainerDayLines {
  /** Every live drop of the container, every group, in send order. */
  drops: DropListLine[];
  /** Groups with no live drop whose list of the day still reads, with why. */
  idle: Array<{ group: BoardGroupRow; name: string }>;
  /** Lists of today's sets no live drop of ANY group uses (the hover's "—" lines), bar the ones an idle line shows. */
  unused: number;
  /** The hover: each list of today once, in send order. */
  order: TodayListEntry[];
  /** The hover's title: the group's name, or "N groups". */
  label: string;
}

const byTime = (a: DropListLine, b: DropListLine) => a.minute - b.minute || a.atUtc.localeCompare(b.atUtc);
/** Sent first (by time), then skipped/stopped, then not planned — as one group's order reads. */
const rank = (e: TodayListEntry) => (e.minute !== null ? 0 : e.off ? 1 : 2);
const byOrder = (a: TodayListEntry, b: TodayListEntry) => rank(a) - rank(b) || (a.minute ?? 0) - (b.minute ?? 0);

function joined(a: TodayListEntry, b: TodayListEntry): TodayListEntry {
  const minute = a.minute === null ? b.minute : b.minute === null ? a.minute : Math.min(a.minute, b.minute);
  const off = minute !== null ? null : a.off === 'stopped' || b.off === 'stopped' ? 'stopped' : (a.off ?? b.off);
  return { ...a, name: a.name ?? b.name, minute, sends: a.sends + b.sends, off };
}

function mergedOrder(groups: BoardGroupRow[]): TodayListEntry[] {
  const out = new Map<number, TodayListEntry>();
  for (const entry of groups.flatMap((g) => g.todayOrder)) {
    const prev = out.get(entry.wiserId);
    out.set(entry.wiserId, prev ? joined(prev, entry) : entry);
  }
  return [...out.values()].sort(byOrder);
}

export function containerDayLines(groups: BoardGroupRow[]): ContainerDayLines {
  const idle = groups.flatMap((group) => (group.sequence.length === 0 && group.listOfDay ? [{ group, list: group.listOfDay }] : []));
  const shown = new Set(idle.map(({ list }) => list.wiserId));
  const order = mergedOrder(groups);
  return {
    drops: groups.flatMap((g) => g.sequence).sort(byTime),
    idle: idle.map(({ group, list }) => ({ group, name: listName(list) })),
    // Counted on the merged order (Bugbot #330): a list another group sends is not "not used".
    unused: order.filter((e) => e.minute === null && !shown.has(e.wiserId)).length,
    order,
    label: groups.length === 1 ? (groups[0]?.groupName ?? '') : `${groups.length} groups`,
  };
}
